/**
 * Pure helpers for the API reference page. No React, no network.
 * See .claude/docs/superpowers/specs/2026-09-09-api-reference-page-design.md §3.2
 */

const ID_FILTER_ORDER = [
  "observatory", "instrument", "region", "repository", "quantity",
  "spectral_range", "format", "instrument_type", "measurement_type",
];

/** Order of suffix chips wherever they appear. */
export const SUFFIX_ORDER = ["!", "~", ">", "<"];

export const CATALOG_ORDER = ["observatories", "instruments", "datasets"];

export const RESOURCE_LABELS = {
  observatories: "Observatories",
  instruments: "Instruments",
  datasets: "Datasets",
  regions: "Regions",
  quantities: "Quantities",
  spectralranges: "Spectral ranges",
  measurementtypes: "Measurement types",
  instrumenttypes: "Instrument types",
  formats: "Formats",
  repositories: "Repositories",
};

export const RESOURCE_SINGULAR = {
  observatories: "observatory",
  instruments: "instrument",
  datasets: "dataset",
  regions: "region",
  quantities: "quantity",
  spectralranges: "spectral range",
  measurementtypes: "measurement type",
  instrumenttypes: "instrument type",
  formats: "format",
  repositories: "repository",
};

function capitalize(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function labelFor(tag) {
  return RESOURCE_LABELS[tag] || capitalize(tag);
}

function singularFor(tag) {
  if (RESOURCE_SINGULAR[tag]) return RESOURCE_SINGULAR[tag];
  return tag.endsWith("s") ? tag.slice(0, -1) : tag;
}

/** "datasets", "datasets/", "datasets/ACE" -> "datasets"; "" -> "" */
export function resourceFromPath(splat) {
  return (splat || "").split("/").filter(Boolean)[0] || "";
}

/** Split "/api/observatories/{id}/instruments/" into ["observatories", "{id}", "instruments"]. */
function pathSegments(path) {
  return path.replace(/^\/api\//, "").split("/").filter(Boolean);
}

/** True when the path still has an OpenAPI placeholder like {id}. */
export function hasPathParams(path) {
  return /\{[^}]+\}/.test(path);
}

/** "/api/datasets/{id}/" + "ACE" -> "/api/datasets/ACE/" */
export function fillPathParams(path, id) {
  return path.replace(/\{[^}]+\}/g, encodeURIComponent(String(id)));
}

/** First record's id from a list response, or null when there is none. */
export function sampleIdFrom(data) {
  const first = Array.isArray(data) ? data[0] : null;
  return first && first.id != null ? first.id : null;
}

function operationLabel(path, tag) {
  const segs = pathSegments(path);
  const label = labelFor(tag).toLowerCase();
  const singular = singularFor(tag);
  if (segs.length === 1) return `List ${label}`;
  if (segs.length === 2 && segs[1] === "{id}") return `Retrieve one ${singular}`;
  if (segs.length === 3 && segs[1] === "{id}") {
    const article = /^[aeiou]/i.test(singular) ? "an" : "a";
    return `${capitalize(segs[2])} of ${article} ${singular}`;
  }
  return path;
}

/** Drop the "Returned ... list contains ..." sentence; the fields table restates it. */
function cleanDescription(text) {
  return (text || "")
    .split(/(?<=\.)\s+/)
    .filter((sentence) => !/^Returned .* contains/i.test(sentence))
    .join(" ")
    .trim();
}

/**
 * Turn one OpenAPI query parameter into a filter row.
 * Suffix support is only stated in prose ("Supports the '!' exclude suffix, and '~' contains-match."),
 * so it is parsed out of the description and removed from the displayed text.
 */
export function parseFilter(param) {
  const raw = param.description || "";
  const supportsIdx = raw.search(/\bSupports\b/);
  const description = (supportsIdx >= 0 ? raw.slice(0, supportsIdx) : raw).trim();
  const clause = supportsIdx >= 0 ? raw.slice(supportsIdx) : "";
  const suffixes = SUFFIX_ORDER.filter((s) => clause.includes(`'${s}'`));
  const schemaType = (param.schema && param.schema.type) || "string";
  const type = /YYYY-MM-DD/.test(raw) ? "date" : schemaType;
  return { name: param.name, type, description, suffixes };
}

function filterClass(f) {
  if (ID_FILTER_ORDER.includes(f.name)) return 0;
  if (f.type === "date") return 1;
  return 2;
}

/** id-like first (fixed order), then dates, then text; alphabetical within the last two. */
export function sortFilters(filters) {
  return [...filters].sort((a, b) => {
    const ca = filterClass(a), cb = filterClass(b);
    if (ca !== cb) return ca - cb;
    if (ca === 0) return ID_FILTER_ORDER.indexOf(a.name) - ID_FILTER_ORDER.indexOf(b.name);
    return a.name.localeCompare(b.name);
  });
}

function deref(ref, components) {
  const name = (ref || "").split("/").pop();
  return { name, schema: components && components.schemas ? components.schemas[name] : undefined };
}

function fieldType(prop, components) {
  if (prop.$ref) return deref(prop.$ref, components).name;
  if (prop.type === "array" && prop.items) {
    return prop.items.$ref ? `${deref(prop.items.$ref, components).name}[]` : `${prop.items.type || "object"}[]`;
  }
  return prop.type || "object";
}

/** One component schema (an object with properties) -> [{ name, type, nullable }]. */
function schemaFields(schema, components) {
  if (!schema || !schema.properties) return [];
  return Object.entries(schema.properties).map(([name, prop]) => ({
    name,
    type: fieldType(prop, components),
    nullable: Boolean(prop.nullable),
  }));
}

/** Response schema ($ref, or array of $ref) -> [{ name, type, nullable }]. [] when it cannot be resolved. */
export function resolveFields(responseSchema, components) {
  if (!responseSchema) return [];
  const ref = responseSchema.$ref || (responseSchema.items && responseSchema.items.$ref);
  if (!ref) return [];
  return schemaFields(deref(ref, components).schema, components);
}

/** Every component schema as a data type: [{ name, fields }], in schema order. */
export function resolveTypes(components) {
  const schemas = (components && components.schemas) || {};
  return Object.entries(schemas).map(([name, schema]) => ({ name, fields: schemaFields(schema, components) }));
}

export function buildApiModel(schema) {
  const resources = {};
  const order = [];
  let endpointCount = 0;

  for (const [path, methods] of Object.entries(schema.paths || {})) {
    const op = methods.get;
    if (!op) continue;
    endpointCount += 1;
    const tag = (op.tags && op.tags[0]) || pathSegments(path)[0];
    if (!resources[tag]) {
      resources[tag] = {
        id: tag,
        label: labelFor(tag),
        singular: singularFor(tag),
        description: "",
        operations: [],
        listOp: null,
        detailOp: null,
      };
      order.push(tag);
    }
    const entry = {
      id: op.operationId || `${tag}_${pathSegments(path).join("_")}`,
      method: "GET",
      path,
      label: operationLabel(path, tag),
      filters: sortFilters((op.parameters || []).filter((p) => p.in === "query").map(parseFilter)),
      fields: resolveFields(
        op.responses && op.responses["200"] && op.responses["200"].content &&
          op.responses["200"].content["application/json"] &&
          op.responses["200"].content["application/json"].schema,
        schema.components
      ),
    };
    const resource = resources[tag];
    resource.operations.push(entry);
    const segments = pathSegments(path);
    if (segments.length === 1) {
      resource.listOp = entry;
      resource.description = cleanDescription(op.description);
    } else if (segments.length === 2 && segments[1] === "{id}") {
      resource.detailOp = entry;
    }
  }

  const catalog = CATALOG_ORDER.filter((id) => resources[id]).map((id) => resources[id]);
  const vocabularies = order.filter((id) => !CATALOG_ORDER.includes(id)).map((id) => resources[id]);

  const types = resolveTypes(schema.components);

  return {
    version: (schema.info && schema.info.version) || "",
    basePath: "/api/",
    types,
    typeNames: new Set(types.map((t) => t.name)),
    groups: [
      { id: "catalog", label: "Catalog", resources: catalog },
      { id: "vocabularies", label: "Vocabularies", resources: vocabularies },
    ],
    resourceCount: order.length,
    endpointCount,
  };
}

export const RESPONSE_ROW_LIMIT = 50;

/** Arrays longer than `limit` are cut; objects pass through. */
export function truncateArray(data, limit = RESPONSE_ROW_LIMIT) {
  if (!Array.isArray(data) || data.length <= limit) return { shown: data, hidden: 0 };
  return { shown: data.slice(0, limit), hidden: data.length - limit };
}

// Escapes for element CONTENT only (text nodes); it is not attribute-value safe
// (doesn't escape quotes) and must not be reused to build attribute values.
function escapeHtml(s) {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Pretty-printed JSON text -> HTML with span classes. Input must already be JSON text
 * (JSON.stringify(data, null, 2)); every token is escaped, so the result is safe for innerHTML.
 */
export function highlightJson(text) {
  const token = /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null)|([{}[\],])/g;
  let out = "";
  let last = 0;
  let m;
  while ((m = token.exec(text)) !== null) {
    out += escapeHtml(text.slice(last, m.index));
    if (m[1] !== undefined) {
      const cls = m[2] ? "apiJsonKey" : "apiJsonString";
      out += `<span class="${cls}">${escapeHtml(m[1])}</span>`;
      if (m[2]) out += `<span class="apiJsonPunct">${escapeHtml(m[2])}</span>`;
    } else if (m[3] !== undefined) {
      out += `<span class="apiJsonNumber">${escapeHtml(m[3])}</span>`;
    } else {
      out += `<span class="apiJsonPunct">${escapeHtml(m[4])}</span>`;
    }
    last = m.index + m[0].length;
  }
  return out + escapeHtml(text.slice(last));
}

export function curlFor(origin, path) {
  return `curl '${origin}${path}'`;
}

/** Whatever the user typed -> a path starting with /api/. Never rejects. */
export function normalizeRequestPath(input) {
  let p = (input || "").trim();
  if (p.startsWith("/api/")) return p;
  if (p === "/api") return "/api/";
  p = p.replace(/^\/+/, "");
  p = p.replace(/^api\/?/, "");
  return `/api/${p}`;
}
